// ─────────────────────────────────────────────────────────────────────────────
// V2Shell — the logged-in frame for the v2 design.
//   • floating icon rail; the active group wears a blue disc that springs
//     between positions (interruptible, starts from its live position)
//   • groups open a frosted GlassMenu of pill options to the right of the rail
//   • translucent top bar with breadcrumb, ⌘K page search, alerts, account
//   • phone: floating liquid-glass tab bar + "More" bottom sheet
//   • every route change cross-fades + lifts the page in (critically damped)
// Routes, permissions, logout and notification calls are identical to v1.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { NAV_GROUPS, ACCOUNT_PAGES, visibleGroup, locate, ADMIN_GROUPS } from "./navConfig";
import { Glyph, Tile } from "./icons";
import { GlassMenu, Sheet, Segmented, useMedia } from "./primitives";
import { useShortcuts, ShortcutHud, ShortcutsSheet, goHint, newHint, accountHint } from "./shortcuts";
import { Spring, prefersReducedMotion } from "./spring";
import { useAppearance, AppearanceSheet } from "./appearance";
import "./v2.css";
import "./v2-pages.css";

const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

// Shared quick-create actions (top bar "New" menu, tab bar "+").
export function useCreateActions() {
  const { hasPermission } = useContext(userContext);
  return useMemo(() => [
    { label: "Invoice", glyph: "invoice", to: "/dashboard/createinvoice", module: "invoices" },
    { label: "Purchase entry", glyph: "cart", to: "/dashboard/purchase-entry", module: "purchases" },
    { label: "Expense", glyph: "wallet", to: "/dashboard/expenses", module: "expenses" },
    { label: "Client", glyph: "clients", to: "/dashboard/manageclients", module: "clients" },
    { label: "Product", glyph: "box", to: "/dashboard/managestock", module: "stock" },
  ].filter((a) => hasPermission(a.module, "write") || hasPermission(a.module, "read")), [hasPermission]);
}

// ── Rail ─────────────────────────────────────────────────────────────────────
function Rail({ groups, activeGroupId, pathname, footer, homeTo = "/dashboard/home" }) {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const discRef = useRef(null);
  const btnRefs = useRef({});
  const spring = useRef(null);
  const [openId, setOpenId] = useState(null);

  // Layout effect (not effect) so the spring exists before the first placeDisc layout effect below.
  useLayoutEffect(() => {
    spring.current = new Spring(0, {
      response: 0.42, dampingRatio: 1,
      onUpdate: (y) => { if (discRef.current) discRef.current.style.transform = `translateY(${y}px)`; },
    });
    return () => spring.current.stop();
  }, []);

  // Move the disc to the active group. First placement jumps; later ones glide.
  const placed = useRef(false);
  const placeDisc = useCallback((animate) => {
    const btn = btnRefs.current[activeGroupId];
    if (!discRef.current || !spring.current) return;
    if (!btn) { discRef.current.style.opacity = "0"; return; }
    discRef.current.style.opacity = "1";
    const y = btn.offsetTop + btn.offsetHeight / 2 - discRef.current.offsetHeight / 2;
    if (animate) spring.current.to(y); else spring.current.set(y);
  }, [activeGroupId]);

  useLayoutEffect(() => { placeDisc(placed.current); placed.current = true; }, [placeDisc, groups.length]);
  useEffect(() => {
    const ro = new ResizeObserver(() => placeDisc(false));
    if (listRef.current) ro.observe(listRef.current);
    return () => ro.disconnect();
  }, [placeDisc]);

  const openGroup = groups.find((g) => g.id === openId);
  const close = useCallback(() => setOpenId(null), []);

  return (
    <aside className="v2-rail" aria-label="Main navigation">
      <Link to={homeTo} className="v2-rail-logo" aria-label="EazyBilling home">
        <span className="v2-logo-mark">e</span>
      </Link>

      <nav ref={listRef} className="v2-rail-list">
        <span ref={discRef} className="v2-rail-disc" aria-hidden="true" />
        {groups.map((g) => {
          const active = g.id === activeGroupId;
          return (
            <button
              key={g.id}
              ref={(el) => (btnRefs.current[g.id] = el)}
              type="button"
              className={`v2-rail-btn${active ? " is-active" : ""}${openId === g.id ? " is-open" : ""}`}
              aria-label={g.label}
              aria-current={active ? "page" : undefined}
              aria-haspopup={g.items ? "menu" : undefined}
              onClick={() => (g.items ? setOpenId((o) => (o === g.id ? null : g.id)) : navigate(g.to))}
            >
              <Glyph name={g.glyph} size={23} stroke={active ? 1.9 : 1.6} />
              <span className="v2-rail-tip">{g.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="v2-rail-foot">{footer}</div>

      {openGroup && (
        <GlassMenu
          open={!!openId}
          onClose={close}
          anchorRef={{ current: btnRefs.current[openGroup.id] }}
          placement="right-start"
          width={256}
          header={
            <div className="v2-menu-title">
              <Tile name={openGroup.glyph} tint={openGroup.tint} size={30} />
              <span>{openGroup.label}</span>
            </div>
          }
          items={openGroup.items.map((i) => ({
            label: i.label, glyph: i.glyph, to: i.to, hint: goHint(i.to),
            active: pathname === i.to || pathname.startsWith(i.to + "/"),
          }))}
        />
      )}
    </aside>
  );
}

// ── Command palette (⌘K) ─────────────────────────────────────────────────────
function CommandPalette({ open, onClose, pages }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const listRef = useRef(null);
  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return pages;
    return pages.filter((p) => `${p.label} ${p.group || ""}`.toLowerCase().includes(s));
  }, [q, pages]);

  useEffect(() => { if (open) { setQ(""); setHi(0); } }, [open]);
  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-i="${hi}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const go = (p) => { onClose(); navigate(p.to); };

  return (
    <Sheet open={open} onClose={onClose} title="Go to" subtitle="Jump to any page" width={560}>
      <div className="v2-cmd">
        <div className="v2-field v2-field--search">
          <Glyph name="search" size={18} />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…"
            aria-label="Search pages"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
              if (e.key === "Enter" && results[hi]) go(results[hi]);
            }} />
        </div>
        <div ref={listRef} className="v2-cmd-list" role="listbox">
          {results.length === 0 && <div className="v2-empty">No pages match “{q}”.</div>}
          {results.map((p, i) => (
            <button key={p.to} type="button" data-i={i} role="option" aria-selected={i === hi}
              className={`v2-cmd-item${i === hi ? " is-hi" : ""}`}
              onPointerEnter={() => setHi(i)} onClick={() => go(p)}>
              <Tile name={p.glyph} tint={p.tint} size={30} />
              <span className="v2-cmd-label">{p.label}</span>
              {p.group && <span className="v2-cmd-group">{p.group}</span>}
              <Glyph name="chevronRight" size={16} className="v2-cmd-chev" />
            </button>
          ))}
        </div>
      </div>
    </Sheet>
  );
}

// ── Notifications (same endpoints + 30s poll as v1) ─────────────────────────
function useNotifications(token, ctxUser) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [c, i] = await Promise.all([
        fetch(`${API_BASE}/api/notifications/unread-count`, { headers: { "auth-token": token } }),
        fetch(`${API_BASE}/api/notifications?limit=5`, { headers: { "auth-token": token } }),
      ]);
      if (!c.ok || !i.ok) return;
      const cd = await c.json();
      const id = await i.json();
      setUnread(cd.count || 0);
      setItems(id.notifications || []);
      setEnabled(true);
    } catch { /* non-enterprise — silent */ }
  }, [token]);
  const markRead = async (nid) => {
    try {
      await fetch(`${API_BASE}/api/notifications/${nid}/read`, { method: "POST", headers: { "auth-token": token } });
      fetchAll();
    } catch { /* silent */ }
  };
  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [fetchAll, ctxUser]);
  return { unread, items, enabled, markRead };
}

// ══════════════════════════════════════════════════════════════════════════════
// variant="admin" → the super admin console: admin navigation, page passed as children,
// no company-only chrome (create menu, account pages, G/N/, shortcut sequences).
export default function V2Shell({ onLogout, variant, children }) {
  const admin = variant === "admin";
  const { user: ctxUser, getUser, hasPermission, isAdmin } = useContext(userContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isPhone = useMedia("(max-width: 860px)");
  const token = localStorage.getItem("token");
  const [appearance, setAppearance] = useAppearance();

  useEffect(() => { getUser(); }, []); // eslint-disable-line

  const groups = useMemo(
    () => (admin ? ADMIN_GROUPS : NAV_GROUPS.map((g) => visibleGroup(g, hasPermission)).filter(Boolean)),
    [hasPermission, ctxUser, admin] // eslint-disable-line
  );
  const homeTo = admin ? "/superadmin" : "/dashboard/home";
  const here = locate(pathname, admin ? ADMIN_GROUPS : undefined);
  const activeGroupId = here?.group?.id;

  const pages = useMemo(() => {
    const out = [];
    groups.forEach((g) => {
      if (g.to) out.push({ label: g.label, to: g.to, glyph: g.glyph, tint: g.tint });
      (g.items || []).forEach((i) => out.push({ label: i.label, to: i.to, glyph: i.glyph, tint: g.tint, group: g.label }));
    });
    if (!admin) ACCOUNT_PAGES.filter((p) => !p.adminOnly || isAdmin()).forEach((p) =>
      out.push({ label: p.label, to: p.to, glyph: p.glyph, tint: "graphite", group: "Account" }));
    return out;
  }, [groups, isAdmin, admin]);

  const name = ctxUser?.name || "";
  const company = ctxUser?.companyName || "";
  const initials = (company || name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const isSuperAdmin = ctxUser?.role === "superadmin";
  const activeEntityName = localStorage.getItem("activeEntityName");

  const notif = useNotifications(token, ctxUser);

  // ── Menus / sheets state ───────────────────────────────────────────────────
  const [acctOpen, setAcctOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [lookOpen, setLookOpen] = useState(false);
  const acctRef = useRef(null);
  const railAcctRef = useRef(null);
  const bellRef = useRef(null);
  const [acctAnchor, setAcctAnchor] = useState(null);

  // ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setCmdOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Top bar materialises once content scrolls beneath it.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Page transition: lift + fade in, from the live state (cancel any in-flight one).
  const mainRef = useRef(null);
  const kbdPerms = { hasPermission, isAdmin, flags: { notifications: !!notif.enabled } };
  const kbd = useShortcuts({ mainRef, navigate, pathname, groups, ...kbdPerms, sequences: !admin, actions: { appearance: () => setLookOpen(true) } });
  const anim = useRef(null);

  // Scroll-edge fades for every .v2-scroll list: fade the bottom while more content sits
  // below, fade the top once scrolled; nothing when the list fits. One capture listener +
  // a mutation observer (lists mount/refresh with data), batched to one frame.
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;
    const mark = (el) => {
      const more = el.scrollHeight - el.clientHeight > 2;
      el.dataset.top = !more || el.scrollTop <= 2 ? "1" : "0";
      el.dataset.end = !more || el.scrollTop + el.clientHeight >= el.scrollHeight - 2 ? "1" : "0";
    };
    let raf = 0;
    const all = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => root.querySelectorAll(".v2-scroll").forEach(mark)); };
    const onScroll = (e) => { if (e.target instanceof Element && e.target.classList.contains("v2-scroll")) mark(e.target); };
    root.addEventListener("scroll", onScroll, true);
    const mo = new MutationObserver(all);
    mo.observe(root, { childList: true, subtree: true });
    window.addEventListener("resize", all);
    all();
    return () => { root.removeEventListener("scroll", onScroll, true); mo.disconnect(); window.removeEventListener("resize", all); cancelAnimationFrame(raf); };
  }, []);
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    const el = mainRef.current;
    if (!el || !el.animate) return;
    anim.current?.cancel();
    const reduce = prefersReducedMotion();
    anim.current = el.animate(
      reduce
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [{ opacity: 0, transform: "translateY(14px)" }, { opacity: 1, transform: "none" }],
      { duration: reduce ? 160 : 520, easing: EASE }
    );
  }, [pathname]);

  const handleExitEntity = async () => {
    try {
      await fetch(`${API_BASE}/api/superadmin/switch/exit`, { method: "POST", headers: { "auth-token": token } });
    } catch { /* silent */ }
    localStorage.removeItem("activeEntityId");
    localStorage.removeItem("activeEntityName");
    navigate("/superadmin");
  };

  const accountItems = [
    ...(isSuperAdmin && !admin ? [{ label: "Super Admin", glyph: "shield", to: "/superadmin" }] : []),
    ...(isSuperAdmin && activeEntityName ? [{ label: `Exit ${activeEntityName}`, glyph: "logout", onSelect: handleExitEntity }] : []),
    ...ACCOUNT_PAGES.filter((p) => !admin && (!p.adminOnly || isAdmin()) && (p.to !== "/dashboard/notifications" || notif.enabled))
      .map((p) => ({ label: p.label, glyph: p.glyph, to: p.to, active: pathname === p.to, hint: accountHint(p.to) })),
    "divider",
    { label: "Appearance", glyph: "sparkle", hint: ", A", onSelect: () => setLookOpen(true) },
    { label: "Keyboard shortcuts", glyph: "grid", hint: "?", onSelect: () => kbd.setHelp(true) },
    "divider",
    { label: "Log out", glyph: "logout", danger: true, onSelect: () => onLogout?.() },
  ];

  const accountHeader = (
    <div className="v2-acct-head">
      <span className="v2-avatar v2-avatar--lg">{initials}</span>
      <span className="v2-acct-text">
        <span className="v2-acct-name">{name || "Account"}</span>
        <span className="v2-acct-meta">
          <span className="v2-chip">{(ctxUser?.role || "user").replace(/^\w/, (c) => c.toUpperCase())}</span>
          {company && <span className="v2-acct-co">{company}</span>}
        </span>
      </span>
    </div>
  );

  const openAccount = (ref) => { setAcctAnchor(ref); setAcctOpen(true); };

  const bellItems = notif.items.length
    ? [
      ...notif.items.map((n) => ({
        label: n.subject, glyph: n.isRead ? "inbox" : "bell",
        hint: new Date(n.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        onSelect: () => { notif.markRead(n._id); navigate(isSuperAdmin ? "/superadmin/notifications" : "/dashboard/notifications"); },
      })),
      "divider",
      { label: "View all", glyph: "arrowUpRight", to: isSuperAdmin ? "/superadmin/notifications" : "/dashboard/notifications" },
    ]
    : [{ label: "No notifications yet", glyph: "inbox", onSelect: () => {} }];

  const createActions = useCreateActions();

  // ── Phone tab bar ──────────────────────────────────────────────────────────
  const tabs = admin ? [
    { id: "sa-overview", label: "Overview", glyph: "grid", to: "/superadmin" },
    { id: "sa-entities", label: "Entities", glyph: "building", to: "/superadmin/entities" },
    { id: "sa-messages", label: "Messages", glyph: "bell", to: "/superadmin/notifications" },
  ] : [
    { id: "home", label: "Home", glyph: "home", to: "/dashboard/home" },
    hasPermission("invoices", "read") && { id: "receivables", label: "Invoices", glyph: "invoice", to: "/dashboard/viewinvoices" },
    { id: "new", label: "New", glyph: "plus" },
    hasPermission("clients", "read") && { id: "clients", label: "Clients", glyph: "clients", to: "/dashboard/manageclients" },
    { id: "more", label: "More", glyph: "grid" },
  ].filter(Boolean);
  const newRef = useRef(null);
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="v2" data-phone={isPhone ? "1" : "0"}>
      <div className="v2-backdrop" aria-hidden="true" />

      {!isPhone && (
        <Rail
          groups={groups}
          activeGroupId={activeGroupId}
          pathname={pathname}
          homeTo={homeTo}
          footer={
            <>
              <button type="button" className="v2-rail-btn" aria-label="Search (Ctrl K)" onClick={() => setCmdOpen(true)}>
                <Glyph name="search" size={22} />
                <span className="v2-rail-tip">Search <kbd>Ctrl K</kbd></span>
              </button>
              <button ref={railAcctRef} type="button" className="v2-rail-btn" aria-label="Settings"
                onClick={() => openAccount(railAcctRef)}>
                <Glyph name="settings" size={22} />
                <span className="v2-rail-tip">Settings</span>
              </button>
            </>
          }
        />
      )}

      <div className="v2-col">
        <header className="v2-topbar" data-scrolled={scrolled ? "1" : "0"}>
          <div className="v2-crumbs">
            {isPhone && (
              <Link to={homeTo} className="v2-rail-logo v2-rail-logo--sm" aria-label="Home">
                <span className="v2-logo-mark">e</span>
              </Link>
            )}
            {here?.group && here.group.items && <span className="v2-crumb-group">{here.group.label}</span>}
            {here?.group && here.group.items && <Glyph name="chevronRight" size={14} className="v2-crumb-sep" />}
            <span className="v2-crumb-page">{here?.item?.label || here?.group?.label || "EazyBilling"}</span>
          </div>

          <div className="v2-top-actions">
            {!isPhone && (
              <button type="button" className="v2-search-pill" onClick={() => setCmdOpen(true)}>
                <Glyph name="search" size={16} />
                <span>Search</span>
                <kbd>Ctrl K</kbd>
              </button>
            )}
            {notif.enabled && (
              <button ref={bellRef} type="button" className="v2-icon-btn v2-glass" aria-label="Notifications"
                onClick={() => setBellOpen((o) => !o)}>
                <Glyph name="bell" size={19} />
                {notif.unread > 0 && <span className="v2-badge">{notif.unread > 9 ? "9+" : notif.unread}</span>}
              </button>
            )}
            <button ref={acctRef} type="button" className="v2-avatar-btn" aria-label="Account"
              onClick={() => openAccount(acctRef)}>
              <span className="v2-avatar">{initials}</span>
            </button>
          </div>
        </header>

        {isSuperAdmin && activeEntityName && (
          <div className="v2-entity-banner">
            <Glyph name="shield" size={16} /> Viewing as <strong>{activeEntityName}</strong>
            <button type="button" className="v2-link" onClick={handleExitEntity}>Exit</button>
          </div>
        )}

        <main ref={mainRef} className="v2-main">
          {children || <Outlet />}
        </main>
      </div>

      {/* Phone: floating glass tab bar */}
      {isPhone && (
        <nav className="v2-tabbar" aria-label="Main">
          {tabs.map((t) => {
            const active = t.to ? (activeGroupId === t.id || pathname === t.to) : (t.id === "more" && moreOpen);
            if (t.id === "new") {
              return (
                <button key="new" ref={newRef} type="button" className="v2-tab v2-tab--new" aria-label="Create new"
                  onClick={() => setNewOpen((o) => !o)}>
                  <span className="v2-tab-plus"><Glyph name="plus" size={22} stroke={2.2} /></span>
                </button>
              );
            }
            return (
              <button key={t.id} type="button" className={`v2-tab${active ? " is-active" : ""}`}
                onClick={() => (t.to ? navigate(t.to) : setMoreOpen(true))}>
                <Glyph name={t.glyph} size={22} stroke={active ? 2 : 1.6} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {!admin && <GlassMenu open={newOpen} onClose={() => setNewOpen(false)} anchorRef={newRef} placement="bottom-start"
        width={230} header={<div className="v2-menu-caption">Create new</div>}
        items={createActions.map((a) => ({ label: a.label, glyph: a.glyph, to: a.to, hint: newHint(a.to) }))} />}

      <GlassMenu open={acctOpen} onClose={() => setAcctOpen(false)} anchorRef={acctAnchor}
        placement={acctAnchor === railAcctRef ? "right-start" : "bottom-end"} width={272}
        header={accountHeader} items={accountItems} />

      <GlassMenu open={bellOpen} onClose={() => setBellOpen(false)} anchorRef={bellRef} width={320}
        header={<div className="v2-menu-caption">Notifications{notif.unread ? ` · ${notif.unread} unread` : ""}</div>}
        items={bellItems} />

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} pages={pages} />

      {/* Phone: everything else */}
      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="All pages">
        <div className="v2-more">
          {groups.map((g) => (
            <div key={g.id} className="v2-more-group">
              {g.items ? (
                <>
                  <div className="v2-more-head"><Tile name={g.glyph} tint={g.tint} size={26} /> {g.label}</div>
                  <div className="v2-inset">
                    {g.items.map((i) => (
                      <button key={i.to} type="button" className="v2-inset-row"
                        onClick={() => { setMoreOpen(false); navigate(i.to); }}>
                        <Glyph name={i.glyph} size={19} /> <span>{i.label}</span>
                        <Glyph name="chevronRight" size={15} className="v2-inset-chev" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="v2-inset">
                  <button type="button" className="v2-inset-row" onClick={() => { setMoreOpen(false); navigate(g.to); }}>
                    <Tile name={g.glyph} tint={g.tint} size={26} /> <span>{g.label}</span>
                    <Glyph name="chevronRight" size={15} className="v2-inset-chev" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </Sheet>

      <AppearanceSheet open={lookOpen} onClose={() => setLookOpen(false)} value={appearance} onChange={setAppearance} />
      <ShortcutHud pending={kbd.pending} perms={kbdPerms} />
      <ShortcutsSheet open={kbd.help} onClose={() => kbd.setHelp(false)} perms={kbdPerms} pageOnly={admin} />
    </div>
  );
}

// Re-export so pages can use the segmented control without a second import path.
export { Segmented };
